/**
 * The part library is where parts are stored.  It expresses the fundamental operations of publishing a document as a
 * part and retrieving a specific version of a part.
 */
import { createPartFromDocument, createPartVersionFromDocument, headVersionName, partVersionFromPublishedDocument, } from "@buildwithflux/models";
import { guid } from "@buildwithflux/shared";
import { enhancePartVersionWithStandardSymbolPreview } from "../functions/parametricPreview";
function createPartVersionCache() {
    return { partVersions: {} };
}
function getPartVersionFromCache(partUid, partVersionKey, cache) {
    const versions = cache.partVersions[partUid];
    if (versions)
        return versions[partVersionKey];
    return undefined;
}
function storePartVersionInCache(partVersionData, cache) {
    const partUid = partVersionData.part_uid;
    const partVersionKey = partVersionData.version;
    if (cache.partVersions[partUid]) {
        const pv = cache.partVersions[partUid];
        if (pv)
            pv[partVersionKey] = partVersionData;
    }
    else {
        cache.partVersions[partUid] = { [partVersionKey]: partVersionData };
    }
}
/**
 * Publishes a new version for a document that is already published as a part.
 * @remarks Because we already know the document is published, we don't need to traverse the part collection - but
 * we _do_ need to update the part with the new part version data.
 */
async function publishNewPartVersion(document, partRepository, partVersionRepository, documentRepository, changeNotes, onPublish) {
    const basePartVersion = partVersionFromPublishedDocument(document, guid());
    // Enhance the part version with a standard symbol preview if no custom preview is set.
    // This uses pre-existing SVG files from the CDN for standard electrical symbols (resistor, capacitor, etc.)
    const newPartVersion = enhancePartVersionWithStandardSymbolPreview(basePartVersion);
    const updatedPartFields = {
        latest_version: newPartVersion.version,
        updated_at: Date.now(),
        latest_version_notes: changeNotes,
    };
    await partVersionRepository.save(newPartVersion);
    await partRepository.update(document.belongs_to_part_uid, updatedPartFields);
    // Sync the document's preview_image with the part version's preview_image.
    // This ensures the preview shows correctly in places that read from the document (e.g., profile pages, project panel).
    // For parts with standard symbols, this sets the symbol path.
    // For parts without standard symbols, this sets preview_image to empty string so the frontend's
    // fallback logic (DocumentStorageHelper.getPreviewUrl) can return ModuleIcon for published parts.
    // Note: We use empty string "" instead of undefined because Firestore ignores undefined values in updates.
    const newPreviewImage = newPartVersion.preview_image ?? "";
    if (newPreviewImage !== document.preview_image) {
        await documentRepository.update(document.uid, { preview_image: newPreviewImage });
    }
    // Synchronously create a snapshot so it is guaranteed to exist before the client needs it.
    // The backend also creates a snapshot asynchronously via submitNonHeadPartVersionToPipeline;
    // snapshot creation is idempotent, so both paths can safely run.
    if (onPublish) {
        await onPublish({ documentUid: document.uid, documentVersion: newPartVersion.version });
    }
    return newPartVersion;
}
async function addPartToLibrary(document, partRepository, documentRepository, onPublish) {
    const newPartVersionKey = guid();
    const newPart = createPartFromDocument(document, newPartVersionKey);
    const basePartVersion = createPartVersionFromDocument(document, newPart.uid, newPartVersionKey);
    // Enhance the part version with a standard symbol preview if no custom preview is set.
    // This uses pre-existing SVG files from the CDN for standard electrical symbols (resistor, capacitor, etc.)
    const newPartVersion = enhancePartVersionWithStandardSymbolPreview(basePartVersion);
    // NOTE: the order of these saves could be important
    // QUESTION: wouldn't it be nice if we could do this in a transaction?
    await partRepository.publishNewPart(newPart, newPartVersion);
    // Update the document with the part UID and sync the preview_image with the part version.
    // For parts with standard symbols, this sets the symbol path.
    // For parts without standard symbols, this sets preview_image to empty string so the frontend's
    // fallback logic (DocumentStorageHelper.getPreviewUrl) can return ModuleIcon for published parts.
    // Note: We use empty string "" instead of undefined because Firestore ignores undefined values in updates.
    const newPreviewImage = newPartVersion.preview_image ?? "";
    const documentUpdate = { belongs_to_part_uid: newPart.uid };
    if (newPreviewImage !== document.preview_image) {
        documentUpdate.preview_image = newPreviewImage;
    }
    await documentRepository.update(document.uid, documentUpdate);
    // Synchronously create a snapshot so it is guaranteed to exist before the client needs it.
    // The backend also creates a snapshot asynchronously via submitNonHeadPartVersionToPipeline;
    // snapshot creation is idempotent, so both paths can safely run.
    if (onPublish) {
        await onPublish({ documentUid: document.uid, documentVersion: newPartVersion.version });
    }
    return { part: newPart, partVersion: newPartVersion };
}
async function getPartByDescriptor(descriptor, staticPartVersionRepository, fallbackPartVersionRepository, partRepository, cache) {
    const { partUid, partVersion } = descriptor;
    let versionToRetrieve = partVersion;
    // Get actual part version key if it is string "latest"
    if (partVersion === "latest") {
        const latest_version = await partRepository.get(partUid).then((partData) => {
            return partData?.latest_version;
        });
        if (latest_version) {
            versionToRetrieve = latest_version;
        }
    }
    if (versionToRetrieve !== headVersionName) {
        // Retrieve from in-memory cache if hit
        const maybeCached = getPartVersionFromCache(partUid, versionToRetrieve, cache);
        if (maybeCached)
            return maybeCached;
        // Retrieve from cdn if hit
        const maybeCdnHit = await staticPartVersionRepository.get(descriptor);
        if (maybeCdnHit) {
            storePartVersionInCache(maybeCdnHit, cache);
            return maybeCdnHit;
        }
    }
    // Fallback to retrieve from GCD. NOTE that we always retrieve "head" version
    // from GCD, because we never store it in CDN, and its pointless to store
    // it in in-memory cache.
    // Later, we should get rid of "head" version and subscribe to that document
    // directly
    const result = await fallbackPartVersionRepository.get(descriptor.partUid, versionToRetrieve);
    if (versionToRetrieve !== headVersionName) {
        // Only store in in-memory cache if not head version
        storePartVersionInCache(result, cache);
    }
    return result;
}
export function createPartLibrary(partRepository, partVersionRepository, staticPartVersionRepository, documentRepository, partVersionCache = createPartVersionCache(), onPublish) {
    return {
        get: (descriptor) => getPartByDescriptor(descriptor, staticPartVersionRepository, partVersionRepository, partRepository, partVersionCache),
        publishToLibrary: (document) => addPartToLibrary(document, partRepository, documentRepository, onPublish),
        publishChangesAsVersion: (document, changeNotes) => publishNewPartVersion(document, partRepository, partVersionRepository, documentRepository, changeNotes, onPublish),
    };
}
//# sourceMappingURL=library.js.map